import React, { useState, useEffect } from 'react';
import {
    Bell,
    Plus,
    Trash2,
    Image as ImageIcon,
    Target,
    Users,
    Navigation,
    X,
    MessageCircle,
    Lock,
    Send,
    Tag,
    Calendar,
    Search,
    Megaphone,
    Zap,
    Gift,
    Percent,
    Eye,
    Clock,
    CheckCircle2,
    Layout,
    Type,
    Sparkles,
    ChevronDown,
    ChevronUp,
    Pencil
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../supabase/config';
import { logActivity } from '../../utils/activityLogger';
import { useAlert } from '../../context/AlertContext';
import { useLocation, useNavigate } from 'react-router-dom';

// ─── 16:9 Görsel Kırpma ───
const cropTo16x9 = (file) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const targetW = 960;
            const targetH = 540; // 16:9
            const canvas = document.createElement('canvas');
            canvas.width = targetW;
            canvas.height = targetH;
            const ctx = canvas.getContext('2d');
            const srcRatio = img.width / img.height;
            const dstRatio = targetW / targetH;
            let sx = 0, sy = 0, sw = img.width, sh = img.height;
            if (srcRatio > dstRatio) {
                sw = img.height * dstRatio;
                sx = (img.width - sw) / 2;
            } else {
                sh = img.width / dstRatio;
                sy = (img.height - sh) / 2;
            }
            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);
            canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')), 'image/jpeg', 0.85);
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
};

// ─── Duyuru Şablonları ───
const ANNOUNCEMENT_TEMPLATES = [
    {
        id: 'simple',
        label: 'Basit Duyuru',
        icon: Type,
        description: 'Sadece başlık ve mesaj',
        color: '#6366f1',
        fields: ['title', 'body'],
    },
    {
        id: 'visual',
        label: 'Görselli Duyuru',
        icon: ImageIcon,
        description: 'Görsel, başlık ve açıklama',
        color: '#10b981',
        fields: ['title', 'body', 'imageUrl'],
    },
    {
        id: 'promo',
        label: 'Promosyon Duyurusu',
        icon: Sparkles,
        description: 'Görsel, başlık, açıklama ve buton',
        color: '#f59e0b',
        fields: ['title', 'body', 'imageUrl', 'actionLabel', 'actionLink'],
    },
];

const Notifications = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { userData, effectiveTenantId, user, hasPermission, permissionsLoading, tenantData } = useAuth();
    const { showAlert, showConfirm } = useAlert();

    // Tab from URL
    const activeTab = location.pathname.includes('/notifications/bildirimler') ? 'bildirimler' : 'duyurular';

    // ─── Shared State ───
    const [reachableCount, setReachableCount] = useState(0);
    const [loading, setLoading] = useState(true);

    // ─── Duyurular State ───
    const [announcements, setAnnouncements] = useState([]);
    const [isAnnouncementModalOpen, setAnnouncementModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState('simple');
    const [announcementForm, setAnnouncementForm] = useState({
        title: '', body: '', imageUrl: '', target: 'all',
        loyaltyStatus: [], visitRole: [], actionLabel: '', actionLink: ''
    });
    const [announcementSearch, setAnnouncementSearch] = useState('');
    const [imageUploading, setImageUploading] = useState(false);

    // ─── Bildirimler State ───
    const [campaigns, setCampaigns] = useState([]);
    const [sentNotifications, setSentNotifications] = useState([]);
    const [campaignSearch, setCampaignSearch] = useState('');
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);
    const [expandedNotifId, setExpandedNotifId] = useState(null);
    const [campaignTargetOverrides, setCampaignTargetOverrides] = useState({});

    // ─── Fetch reachable count ───
    useEffect(() => {
        if (!effectiveTenantId) return;
        const fetchReachable = async () => {
            const { count } = await supabase
                .from('customers')
                .select('id', { count: 'exact' })
                .eq('tenant_id', effectiveTenantId)
                .eq('notifications_enabled', true);
            setReachableCount(count || 0);
        };
        fetchReachable();
        const channel = supabase
            .channel('notif-reachable')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'customers', filter: `tenant_id=eq.${effectiveTenantId}` }, fetchReachable)
            .subscribe();
        return () => supabase.removeChannel(channel);
    }, [effectiveTenantId]);

    // ─── Fetch announcements (notifications table) ───
    useEffect(() => {
        const fetchAnnouncements = async () => {
            setLoading(true);
            let query = supabase.from('notifications').select('*');
            if (!effectiveTenantId) {
                if (userData?.isSuperAdmin) {
                    query = query.order('created_at', { ascending: false }).limit(1000);
                } else { setLoading(false); return; }
            } else {
                query = query.eq('tenant_id', effectiveTenantId);
            }
            const { data, error } = await query;
            if (error) { console.error(error); setLoading(false); return; }
            const docs = (data || []).map(r => ({
                ...r, tenantId: r.tenant_id, createdAt: r.created_at,
                broadcastedAt: r.broadcasted_at, imageUrl: r.image_url,
                actionLabel: r.action_label, actionLink: r.action_link,
                loyaltyStatus: r.loyalty_status, visitRole: r.visit_role,
                templateType: r.template_type || 'simple'
            }));
            docs.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
            setAnnouncements(docs);
            setLoading(false);
        };
        fetchAnnouncements();
        const filter = effectiveTenantId ? `tenant_id=eq.${effectiveTenantId}` : undefined;
        const channel = supabase
            .channel('notif-list')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications', ...(filter ? { filter } : {}) }, fetchAnnouncements)
            .subscribe();
        return () => supabase.removeChannel(channel);
    }, [effectiveTenantId, userData?.isSuperAdmin]);

    // ─── Fetch campaigns (discounts table) ───
    useEffect(() => {
        if (!effectiveTenantId) { setLoadingCampaigns(false); return; }
        const fetchCampaigns = async () => {
            setLoadingCampaigns(true);
            const { data, error } = await supabase
                .from('discounts')
                .select('*')
                .eq('tenant_id', effectiveTenantId)
                .eq('is_active', true);
            if (error) { console.error(error); setLoadingCampaigns(false); return; }
            setCampaigns((data || []).map(r => ({
                id: r.id, title: r.title, description: r.description,
                type: r.type || 'percentage', value: r.value,
                campaignType: r.campaign_type || 'simple',
                isReward: r.is_reward, isCoupon: r.campaign_type === 'coupon' || r.campaign_type === 'coupon_bundle',
                requiredPoints: r.required_points, rewardType: r.reward_type,
                startDate: r.start_date, expiryDate: r.expiry_date,
                target: r.target || 'all', loyaltyStatus: r.loyalty_status || [],
                visitRole: r.visit_role || [], isActive: r.is_active
            })));
            setLoadingCampaigns(false);
        };
        fetchCampaigns();

        // Fetch sent campaign notifications
        const fetchSent = async () => {
            const { data } = await supabase
                .from('notifications')
                .select('*')
                .eq('tenant_id', effectiveTenantId)
                .not('campaign_id', 'is', null)
                .order('created_at', { ascending: false });
            setSentNotifications((data || []).map(r => ({
                ...r, createdAt: r.created_at, broadcastedAt: r.broadcasted_at,
                campaignId: r.campaign_id
            })));
        };
        fetchSent();

        const channel = supabase
            .channel('campaigns-list')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'discounts', filter: `tenant_id=eq.${effectiveTenantId}` }, fetchCampaigns)
            .subscribe();
        return () => supabase.removeChannel(channel);
    }, [effectiveTenantId]);

    // ─── Duyuru Submit ───
    const handleAnnouncementSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                title: announcementForm.title,
                body: announcementForm.body,
                image_url: announcementForm.imageUrl,
                target: announcementForm.target,
                loyalty_status: announcementForm.loyaltyStatus,
                visit_role: announcementForm.visitRole,
                action_label: announcementForm.actionLabel,
                action_link: announcementForm.actionLink,
                template_type: selectedTemplate,
                tenant_id: effectiveTenantId,
                updated_at: new Date().toISOString()
            };
            if (editingId) {
                const { error } = await supabase.from('notifications').update(data).eq('id', editingId);
                if (error) throw error;
            } else {
                const { error } = await supabase.from('notifications').insert({ ...data, created_at: new Date().toISOString() });
                if (error) throw error;
            }
            await logActivity(effectiveTenantId, user?.email || 'N/A', user?.uid,
                editingId ? 'UPDATE_NOTIFICATION' : 'CREATE_ANNOUNCEMENT',
                `${editingId ? 'Updated' : 'Created'} announcement: ${announcementForm.title}`);
            showAlert(editingId ? 'Duyuru güncellendi.' : 'Duyuru oluşturuldu.', 'success');
            setAnnouncementModalOpen(false);
            setEditingId(null);
            resetAnnouncementForm();
        } catch (err) {
            console.error(err);
            showAlert('Duyuru kaydedilirken hata oluştu.', 'error');
        }
    };

    const resetAnnouncementForm = () => {
        setAnnouncementForm({ title: '', body: '', imageUrl: '', target: 'all', loyaltyStatus: [], visitRole: [], actionLabel: '', actionLink: '' });
        setSelectedTemplate('simple');
    };

    const handleAnnouncementImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !effectiveTenantId) return;
        setImageUploading(true);
        try {
            const cropped = await cropTo16x9(file);
            const filePath = `${effectiveTenantId}/notif_${Date.now()}.jpg`;
            const { error: uploadError } = await supabase.storage
                .from('logos')
                .upload(filePath, cropped, { upsert: true, contentType: 'image/jpeg' });
            if (uploadError) throw uploadError;
            const { data } = supabase.storage.from('logos').getPublicUrl(filePath);
            setAnnouncementForm(prev => ({ ...prev, imageUrl: data.publicUrl }));
        } catch (err) {
            console.error('Image upload error:', err);
            showAlert('Görsel yüklenirken hata oluştu.', 'error');
        } finally {
            setImageUploading(false);
            e.target.value = '';
        }
    };

    const handleEditAnnouncement = (notif) => {
        setEditingId(notif.id);
        setSelectedTemplate(notif.templateType || 'simple');
        setAnnouncementForm({
            title: notif.title, body: notif.body, imageUrl: notif.imageUrl || '',
            target: notif.target || 'all', loyaltyStatus: notif.loyaltyStatus || [],
            visitRole: notif.visitRole || [], actionLabel: notif.actionLabel || '',
            actionLink: notif.actionLink || ''
        });
        setAnnouncementModalOpen(true);
    };

    const handleBroadcastAnnouncement = async (notif) => {
        if (!await showConfirm(`${reachableCount} müşteriye push bildirimi gönderilecek. Onaylıyor musunuz?`, { title: 'Duyuru Gönder', confirmText: 'Gönder', type: 'info' })) return;
        try {
            const { error: updateErr } = await supabase.from('notifications')
                .update({ broadcasted_at: new Date().toISOString(), status: 'Sent' }).eq('id', notif.id);
            if (updateErr) throw updateErr;
            const { error: insertErr } = await supabase.from('fcm_broadcasts').insert({
                notification_id: notif.id, tenant_id: effectiveTenantId,
                title: notif.title, body: notif.body, image_url: notif.imageUrl || null,
                action_label: notif.actionLabel || null, action_link: notif.actionLink || null,
                target: notif.target || 'all', loyalty_status: notif.loyaltyStatus || [],
                visit_role: notif.visitRole || [], created_at: new Date().toISOString(), processed: false
            });
            if (insertErr) throw insertErr;
            await logActivity(effectiveTenantId, user?.email || 'N/A', user?.uid, 'BROADCAST_ANNOUNCEMENT',
                `Broadcasted announcement to ${reachableCount} devices: ${notif.title}`);
            showAlert(`${reachableCount} cihaz için gönderim sıraya alındı.`, 'success');
        } catch (err) {
            console.error(err);
            showAlert('Gönderim sırasında bir hata oluştu.', 'error');
        }
    };

    const handleDeleteAnnouncement = async (notif) => {
        if (await showConfirm('Bu duyuruyu silmek istediğinize emin misiniz?', { title: 'Duyuruyu Sil', confirmText: 'Sil', type: 'error' })) {
            try {
                const { error } = await supabase.from('notifications').delete().eq('id', notif.id);
                if (error) throw error;
                await logActivity(effectiveTenantId, user?.email || 'N/A', user?.uid, 'DELETE_ANNOUNCEMENT', `Deleted announcement: ${notif.title}`);
            } catch (err) {
                console.error(err);
                showAlert('Silme sırasında hata oluştu.', 'error');
            }
        }
    };

    // ─── Kampanya Bildirimi Gönder ───
    const buildCampaignMessage = (campaign) => {
        if (campaign.isReward) {
            const pts = campaign.requiredPoints || '?';
            return {
                title: `🎁 Ödül: ${campaign.title}`,
                body: campaign.description
                    ? `${campaign.description} — ${pts} puanınızla hemen kazanın!`
                    : `${pts} puanınızla bu ödülü kazanabilirsiniz!`
            };
        }
        if (campaign.isCoupon) {
            return {
                title: `🎟️ Kupon: ${campaign.title}`,
                body: campaign.description
                    ? `${campaign.description} — Kuponunuzu kullanmayı unutmayın!`
                    : `Özel kupon fırsatınız sizi bekliyor!`
            };
        }
        if (campaign.type === 'free_product') {
            return {
                title: `🎁 ${campaign.title}`,
                body: campaign.description || 'Ücretsiz ürün fırsatını kaçırmayın!'
            };
        }
        if (campaign.type === 'multiplier') {
            return {
                title: `⚡ ${campaign.value}x Puan: ${campaign.title}`,
                body: campaign.description || `Alışverişlerinizde ${campaign.value}x puan kazanın!`
            };
        }
        // percentage / fixed discount
        if (campaign.type === 'percentage' && Number(campaign.value) >= 100) {
            return {
                title: `🎁 Ücretsiz: ${campaign.title}`,
                body: campaign.description || 'Bu fırsatı kaçırmayın!'
            };
        }
        const val = campaign.type === 'percentage' ? `%${campaign.value}` : `${campaign.value}₺`;
        return {
            title: `🎉 ${val} İndirim: ${campaign.title}`,
            body: campaign.description
                ? `${campaign.description}`
                : `${val} indirim fırsatını kaçırmayın!`
        };
    };

    const handleSendCampaignNotification = async (campaign, overrideTarget) => {
        const msg = buildCampaignMessage(campaign);
        const target = overrideTarget || campaign.target || 'all';

        if (!await showConfirm(
            `"${campaign.title}" kampanyası için bildirim gönderilecek.\n\nBaşlık: ${msg.title}\nMesaj: ${msg.body}\nHedef: ${target === 'all' ? 'Tüm Müşteriler' : 'Seçili Kitle'}`,
            { title: 'Kampanya Bildirimi Gönder', confirmText: 'Gönder', type: 'info' }
        )) return;

        try {
            // Insert notification record (campaign-linked)
            const { data: notifData, error: notifErr } = await supabase.from('notifications').insert({
                title: msg.title,
                body: msg.body,
                tenant_id: effectiveTenantId,
                target: target,
                loyalty_status: campaign.notifLoyaltyStatus || campaign.loyaltyStatus || [],
                visit_role: campaign.notifVisitRole || campaign.visitRole || [],
                campaign_id: campaign.id,
                template_type: 'campaign',
                broadcasted_at: new Date().toISOString(),
                status: 'Sent',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            }).select().single();
            if (notifErr) throw notifErr;

            // Insert broadcast
            const { error: broadcastErr } = await supabase.from('fcm_broadcasts').insert({
                notification_id: notifData.id,
                tenant_id: effectiveTenantId,
                title: msg.title,
                body: msg.body,
                target: target,
                loyalty_status: campaign.notifLoyaltyStatus || campaign.loyaltyStatus || [],
                visit_role: campaign.notifVisitRole || campaign.visitRole || [],
                created_at: new Date().toISOString(),
                processed: false
            });
            if (broadcastErr) throw broadcastErr;

            await logActivity(effectiveTenantId, user?.email || 'N/A', user?.uid, 'SEND_CAMPAIGN_NOTIFICATION',
                `Sent campaign notification: ${campaign.title} to ${reachableCount} devices`);
            showAlert(`"${campaign.title}" bildirimi ${reachableCount} cihaza gönderildi.`, 'success');

            // Refresh sent list
            const { data: refreshed } = await supabase
                .from('notifications')
                .select('*')
                .eq('tenant_id', effectiveTenantId)
                .not('campaign_id', 'is', null)
                .order('created_at', { ascending: false });
            setSentNotifications((refreshed || []).map(r => ({
                ...r, createdAt: r.created_at, broadcastedAt: r.broadcasted_at, campaignId: r.campaign_id
            })));
        } catch (err) {
            console.error(err);
            showAlert('Bildirim gönderilirken hata oluştu.', 'error');
        }
    };

    const getCampaignValueText = (c) => {
        if (c.isReward) return `${c.requiredPoints || '?'} puan ile`;
        if (c.type === 'percentage') return Number(c.value) >= 100 ? 'Ücretsiz' : `%${c.value} indirim`;
        if (c.type === 'fixed') return `${c.value}₺ indirim`;
        if (c.type === 'free_product') return 'Ücretsiz ürün';
        if (c.type === 'multiplier') return `${c.value}x puan çarpanı`;
        return `${c.value} indirim`;
    };

    const getCampaignIcon = (c) => {
        if (c.isReward) return Gift;
        if (c.isCoupon) return Tag;
        if (c.type === 'free_product') return Gift;
        if (c.type === 'multiplier') return Zap;
        if (c.type === 'percentage' && Number(c.value) >= 100) return Gift;
        return Percent;
    };

    // ─── Permission Check ───
    if (permissionsLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}><div className="loading-spinner" style={{ width: '32px', height: '32px', border: '3px solid rgba(var(--primary-rgb), 0.1)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /></div>;
    if (!userData?.isSuperAdmin && !hasPermission('notifications', 'view')) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <Lock size={48} style={{ color: 'var(--text-muted)', marginBottom: '1rem', opacity: 0.5 }} />
                <h3>{t('common.accessDenied')}</h3>
                <p style={{ color: 'var(--text-muted)' }}>{t('common.noPermission')}</p>
            </div>
        );
    }

    const currentTemplate = ANNOUNCEMENT_TEMPLATES.find(t => t.id === selectedTemplate) || ANNOUNCEMENT_TEMPLATES[0];

    // ─── Template preview for announcements ───
    const AnnouncementPreview = () => {
        const form = announcementForm;
        return (
            <div style={{ background: 'var(--bg-main)', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden', maxWidth: '320px' }}>
                {currentTemplate.fields.includes('imageUrl') && form.imageUrl && (
                    <div style={{ width: '100%', aspectRatio: '16/9', overflow: 'hidden' }}>
                        <img src={form.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                )}
                <div style={{ padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.75rem' }}>
                        <div style={{ width: '32px', height: '32px', background: `${currentTemplate.color}15`, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Bell size={16} style={{ color: currentTemplate.color }} />
                        </div>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase' }}>Duyuru Önizlemesi</span>
                    </div>
                    <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text-main)' }}>
                        {form.title || 'Başlık buraya gelecek'}
                    </h4>
                    <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: currentTemplate.fields.includes('actionLabel') ? '1rem' : 0 }}>
                        {form.body || 'Mesaj içeriği buraya gelecek...'}
                    </p>
                    {currentTemplate.fields.includes('actionLabel') && form.actionLabel && (
                        <button style={{ padding: '8px 16px', background: 'var(--primary)', color: 'var(--text-on-primary)', border: 'none', borderRadius: '8px', fontSize: '0.8125rem', fontWeight: 700, cursor: 'default' }}>
                            {form.actionLabel}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="fade-in">
            {/* Header */}
            <div style={{ marginBottom: '2rem' }}>
                <h1 style={{ fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>
                    {activeTab === 'bildirimler' ? 'Bildirimler' : 'Duyurular'}
                </h1>
                <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
                    {activeTab === 'bildirimler'
                        ? 'Kampanyalarınızı hazır şablonla bildirim olarak gönderin.'
                        : 'Müşterilerinize özelleştirilebilir duyurular oluşturun ve gönderin.'}
                </p>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', background: 'rgba(var(--primary-rgb), 0.05)', padding: '0.5rem', borderRadius: '16px', width: 'fit-content' }}>
                {[
                    { id: 'bildirimler', label: 'Bildirimler', icon: Bell, path: '/app/notifications/bildirimler' },
                    { id: 'duyurular', label: 'Duyurular', icon: Megaphone, path: '/app/notifications' }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => navigate(tab.path)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '10px 20px', borderRadius: '12px', border: 'none',
                            background: activeTab === tab.id ? 'var(--primary)' : 'transparent',
                            color: activeTab === tab.id ? 'var(--text-on-primary)' : 'var(--text-main)',
                            fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s'
                        }}
                    >
                        <tab.icon size={18} /> {tab.label}
                    </button>
                ))}
            </div>

            {/* Reachable Info */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: 'rgba(16, 185, 129, 0.08)', borderRadius: '10px', marginBottom: '1.5rem', fontSize: '0.8125rem', fontWeight: 600, color: '#10b981' }}>
                <Users size={14} /> {reachableCount} müşteri bildirimlere ulaşılabilir
            </div>

            {/* ════════════════════════════════════════════ */}
            {/* TAB: DUYURULAR */}
            {/* ════════════════════════════════════════════ */}
            {activeTab === 'duyurular' && (
                <div>
                    {/* Toolbar */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '2rem' }}>
                        <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                            <input
                                type="text" placeholder="Duyuru ara..."
                                value={announcementSearch} onChange={(e) => setAnnouncementSearch(e.target.value)}
                                style={{ paddingLeft: '40px', width: '100%', height: '46px', background: 'var(--bg-sidebar)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '12px', outline: 'none' }}
                            />
                        </div>
                        <button
                            onClick={() => { setEditingId(null); resetAnnouncementForm(); setAnnouncementModalOpen(true); }}
                            className="btn-primary"
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '46px', padding: '0 24px', borderRadius: '12px' }}
                        >
                            <Plus size={20} /> Yeni Duyuru
                        </button>
                    </div>

                    {/* Announcement List */}
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
                            <div className="loading-spinner" style={{ width: '32px', height: '32px', border: '3px solid rgba(var(--primary-rgb), 0.1)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                        </div>
                    ) : announcements.filter(a => !a.campaign_id).length === 0 ? (
                        <div className="card glass" style={{ padding: '6rem 2rem', textAlign: 'center', borderRadius: '24px' }}>
                            <div style={{ width: '80px', height: '80px', background: 'rgba(var(--primary-rgb), 0.05)', color: 'var(--primary)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem', opacity: 0.8 }}>
                                <Megaphone size={40} />
                            </div>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Henüz duyuru yok</h3>
                            <p style={{ color: 'var(--text-muted)', maxWidth: '400px', margin: '0 auto', lineHeight: 1.6 }}>
                                Müşterilerinize özel duyurular oluşturup gönderin.
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))', gap: '1.5rem' }}>
                            {announcements
                                .filter(a => !a.campaign_id)
                                .filter(n => n.title?.toLowerCase().includes(announcementSearch.toLowerCase()) || n.body?.toLowerCase().includes(announcementSearch.toLowerCase()))
                                .map(notif => (
                                    <AnnouncementCard
                                        key={notif.id}
                                        notif={notif}
                                        onBroadcast={handleBroadcastAnnouncement}
                                        onEdit={handleEditAnnouncement}
                                        onDelete={handleDeleteAnnouncement}
                                        t={t}
                                        tenantData={tenantData}
                                    />
                                ))}
                        </div>
                    )}
                </div>
            )}

            {/* ════════════════════════════════════════════ */}
            {/* TAB: BİLDİRİMLER */}
            {/* ════════════════════════════════════════════ */}
            {activeTab === 'bildirimler' && (
                <div>
                    {/* Active Campaigns */}
                    <div style={{ marginBottom: '2.5rem' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                            <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                                <input
                                    type="text" placeholder="Kampanya ara..."
                                    value={campaignSearch} onChange={(e) => setCampaignSearch(e.target.value)}
                                    style={{ paddingLeft: '40px', width: '100%', height: '46px', background: 'var(--bg-sidebar)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '12px', outline: 'none' }}
                                />
                            </div>
                        </div>

                        {loadingCampaigns ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
                                <div className="loading-spinner" style={{ width: '32px', height: '32px', border: '3px solid rgba(var(--primary-rgb), 0.1)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                            </div>
                        ) : campaigns.length === 0 ? (
                            <div className="card glass" style={{ padding: '4rem 2rem', textAlign: 'center', borderRadius: '24px' }}>
                                <div style={{ width: '80px', height: '80px', background: 'rgba(var(--primary-rgb), 0.05)', color: 'var(--primary)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', opacity: 0.8 }}>
                                    <Zap size={40} />
                                </div>
                                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>Aktif kampanya yok</h3>
                                <p style={{ color: 'var(--text-muted)', maxWidth: '400px', margin: '0 auto', lineHeight: 1.6 }}>
                                    Kampanyalar sayfasından yeni kampanya oluşturarak bildirim gönderebilirsiniz.
                                </p>
                                <button
                                    onClick={() => navigate('/app/campaigns')}
                                    className="btn-primary"
                                    style={{ marginTop: '1.5rem', padding: '10px 24px', borderRadius: '12px', border: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                >
                                    <Zap size={16} /> Kampanyalara Git
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))', gap: '1.5rem' }}>
                                {campaigns
                                    .filter(c => c.title?.toLowerCase().includes(campaignSearch.toLowerCase()) || c.description?.toLowerCase().includes(campaignSearch.toLowerCase()))
                                    .map(campaign => {
                                        const Icon = getCampaignIcon(campaign);
                                        const override = campaignTargetOverrides[campaign.id] || {};
                                        const currentTarget = override.target || 'all';
                                        const currentLoyalty = override.loyaltyStatus || [];
                                        const currentVisitRole = override.visitRole || [];
                                        const msg = buildCampaignMessage(campaign);
                                        const accentColor = campaign.isReward ? '#f59e0b' : campaign.isCoupon ? '#6366f1' : campaign.type === 'multiplier' ? '#8b5cf6' : '#10b981';

                                        const updateOverride = (patch) => {
                                            setCampaignTargetOverrides(prev => ({
                                                ...prev,
                                                [campaign.id]: { ...prev[campaign.id], ...patch }
                                            }));
                                        };

                                        return (
                                            <div key={campaign.id} className="card glass notification-card-hover" style={{ position: 'relative', transition: 'all 0.3s', padding: 0, overflow: 'hidden' }}>
                                                <div style={{ padding: '1.5rem' }}>
                                                    {/* Header: Type badge + actions */}
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div style={{ width: '36px', height: '36px', background: `${accentColor}15`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <Icon size={18} style={{ color: accentColor }} />
                                                            </div>
                                                            <div>
                                                                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: accentColor, textTransform: 'uppercase' }}>
                                                                    {campaign.isReward ? 'Ödül' : campaign.isCoupon ? 'Kupon' : campaign.type === 'multiplier' ? 'Puan Çarpanı' : 'Kampanya'}
                                                                </span>
                                                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                                                    {getCampaignValueText(campaign)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                const c = { ...campaign, notifLoyaltyStatus: currentLoyalty, notifVisitRole: currentVisitRole };
                                                                handleSendCampaignNotification(c, currentTarget);
                                                            }}
                                                            style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', padding: '8px 12px', borderRadius: '10px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 700 }}
                                                            title="Bildirim Gönder"
                                                        >
                                                            <Send size={14} /> GÖNDER
                                                        </button>
                                                    </div>

                                                    {/* Title */}
                                                    <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem' }}>{campaign.title}</h3>

                                                    {/* Message preview */}
                                                    <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: '1.25rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {msg.body}
                                                    </p>

                                                    {/* Info boxes */}
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <div style={{ flex: 1, padding: '10px 8px', background: 'var(--bg-main)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                                            <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '4px' }}>BİLDİRİM</div>
                                                            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{msg.title}</div>
                                                        </div>
                                                        {campaign.expiryDate && (
                                                            <div style={{ flex: 1, padding: '10px 8px', background: 'var(--bg-main)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                                                <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '4px' }}>BİTİŞ TARİHİ</div>
                                                                <div style={{ fontSize: '0.8125rem', fontWeight: 700 }}>{new Date(campaign.expiryDate).toLocaleDateString('tr-TR')}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Hedef Kitle Seçimi */}
                                                <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border)', background: 'rgba(var(--primary-rgb), 0.02)' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                        <div>
                                                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Kime Gönderilsin?</label>
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                {[{ value: 'all', label: 'Tüm Müşteriler', icon: Users }, { value: 'groups', label: 'Seçili Gruplar', icon: Target }].map(opt => (
                                                                    <button key={opt.value} type="button"
                                                                        onClick={() => updateOverride({ target: opt.value })}
                                                                        style={{
                                                                            display: 'flex', alignItems: 'center', gap: '6px',
                                                                            padding: '8px 16px', borderRadius: '10px',
                                                                            border: currentTarget === opt.value ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
                                                                            background: currentTarget === opt.value ? 'rgba(var(--primary-rgb), 0.08)' : 'var(--bg-card)',
                                                                            color: currentTarget === opt.value ? 'var(--primary)' : 'var(--text-main)',
                                                                            fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer', transition: 'all 0.15s'
                                                                        }}
                                                                    >
                                                                        <opt.icon size={14} /> {opt.label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        {currentTarget === 'groups' && (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                                <div>
                                                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)' }}>Sadakat Seviyeleri</label>
                                                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                                        {(tenantData?.loyalty_levels || tenantData?.loyaltyLevels || [])
                                                                            .filter(l => l.label && l.label.toLowerCase() !== 'loyal')
                                                                            .map(level => {
                                                                                const sel = currentLoyalty.includes(level.label);
                                                                                return (
                                                                                    <button key={level.label} type="button"
                                                                                        onClick={() => updateOverride({ loyaltyStatus: sel ? currentLoyalty.filter(s => s !== level.label) : [...currentLoyalty, level.label] })}
                                                                                        style={{ padding: '5px 12px', fontSize: '0.75rem', fontWeight: 700, background: sel ? level.color : 'transparent', color: sel ? '#fff' : level.color, border: `1.5px solid ${level.color}`, borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s' }}>
                                                                                        {level.label}
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)' }}>Müşteri Rolleri</label>
                                                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                                        {(tenantData?.visit_roles || tenantData?.visitRoles || [])
                                                                            .filter(r => r.label && r.label.toLowerCase() !== 'all')
                                                                            .map(role => {
                                                                                const sel = currentVisitRole.includes(role.label);
                                                                                return (
                                                                                    <button key={role.label} type="button"
                                                                                        onClick={() => updateOverride({ visitRole: sel ? currentVisitRole.filter(r => r !== role.label) : [...currentVisitRole, role.label] })}
                                                                                        style={{ padding: '5px 12px', fontSize: '0.75rem', fontWeight: 700, background: sel ? role.color : 'transparent', color: sel ? 'white' : 'var(--text-main)', border: sel ? `1.5px solid ${role.color}` : '1.5px solid var(--border)', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s' }}>
                                                                                        {role.label}
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </div>

                    {/* Sent Campaign Notifications History */}
                    {sentNotifications.length > 0 && (
                        <div>
                            <h3 style={{ fontSize: '1.125rem', fontWeight: 800, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-main)' }}>
                                <Clock size={18} /> Gönderim Geçmişi
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {sentNotifications.map(notif => {
                                    const targetLabel = notif.target === 'all' ? 'Tüm Müşteriler' : (() => {
                                        const ls = (Array.isArray(notif.loyalty_status) ? notif.loyalty_status : []).filter(Boolean);
                                        const vr = (Array.isArray(notif.visit_role) ? notif.visit_role : []).filter(Boolean);
                                        return [...ls, ...vr].join(', ') || 'Özel Kitle';
                                    })();
                                    return (
                                        <div key={notif.id} style={{
                                            padding: '1rem 1.25rem', background: 'var(--bg-card)', borderRadius: '14px',
                                            border: '1px solid var(--border)',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(16, 185, 129, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <CheckCircle2 size={18} style={{ color: '#10b981' }} />
                                                </div>
                                                <div style={{ flex: 1, minWidth: '200px' }}>
                                                    <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '4px' }}>{notif.title}</div>
                                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{notif.body}</div>
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                        <Calendar size={12} />
                                                        {notif.createdAt ? new Date(notif.createdAt).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                                                    </div>
                                                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <Users size={11} /> {targetLabel}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ════════════════════════════════════════════ */}
            {/* DUYURU MODAL */}
            {/* ════════════════════════════════════════════ */}
            {isAnnouncementModalOpen && (
                <div
                    onClick={() => { setAnnouncementModalOpen(false); setEditingId(null); }}
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(12px)', padding: '1.5rem' }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        className="notification-modal" style={{ width: '100%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-sidebar)', borderRadius: '24px', padding: '2.5rem', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', position: 'relative' }}
                    >
                        {/* Modal Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                            <div>
                                <h2 style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: '0.25rem' }}>
                                    {editingId ? 'Duyuruyu Düzenle' : 'Yeni Duyuru Oluştur'}
                                </h2>
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Şablon seçin ve duyurunuzu özelleştirin.</p>
                            </div>
                            <button onClick={() => { setAnnouncementModalOpen(false); setEditingId(null); }}
                                style={{ background: 'var(--bg-main)', color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '10px', borderRadius: '12px', cursor: 'pointer' }}>
                                <X size={20} />
                            </button>
                        </div>

                        {/* Template Selector */}
                        <div style={{ marginBottom: '2rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Şablon Seçin</label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                {ANNOUNCEMENT_TEMPLATES.map(tmpl => {
                                    const isActive = selectedTemplate === tmpl.id;
                                    return (
                                        <button
                                            key={tmpl.id}
                                            type="button"
                                            onClick={() => setSelectedTemplate(tmpl.id)}
                                            style={{
                                                padding: '1rem', borderRadius: '14px', border: isActive ? `2px solid ${tmpl.color}` : '2px solid var(--border)',
                                                background: isActive ? `${tmpl.color}08` : 'var(--bg-main)',
                                                cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                                                transition: 'all 0.2s', textAlign: 'center'
                                            }}
                                        >
                                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: `${tmpl.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <tmpl.icon size={20} style={{ color: tmpl.color }} />
                                            </div>
                                            <div style={{ fontWeight: 700, fontSize: '0.8125rem', color: isActive ? tmpl.color : 'var(--text-main)' }}>{tmpl.label}</div>
                                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{tmpl.description}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <form onSubmit={handleAnnouncementSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {/* Content + Preview Row */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2rem', alignItems: 'start' }}>
                                {/* Form Fields */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                    <div style={{ background: 'var(--bg-main)', padding: '1.5rem', borderRadius: '16px', border: '1px solid var(--border)' }}>
                                        <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>İçerik</label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                            <div>
                                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>Başlık</label>
                                                <input required placeholder="Örn: Bu Hafta Sonu Sürprizi!" value={announcementForm.title}
                                                    onChange={e => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                                                    style={{ background: 'var(--bg-card)' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>Mesaj</label>
                                                <textarea required placeholder="Mesajınızı buraya yazın..."
                                                    style={{ width: '100%', height: '100px', borderRadius: '12px', padding: '12px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)', resize: 'none' }}
                                                    value={announcementForm.body}
                                                    onChange={e => setAnnouncementForm({ ...announcementForm, body: e.target.value })} />
                                            </div>
                                            {currentTemplate.fields.includes('imageUrl') && (
                                                <div>
                                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>Görsel</label>
                                                    {announcementForm.imageUrl ? (
                                                        <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--border)', marginBottom: '0.5rem' }}>
                                                            <img src={announcementForm.imageUrl} alt="" style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }} />
                                                            <button type="button"
                                                                onClick={() => setAnnouncementForm({ ...announcementForm, imageUrl: '' })}
                                                                style={{ position: 'absolute', top: '8px', right: '8px', width: '30px', height: '30px', borderRadius: '50%', background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 }}>
                                                                <X size={16} strokeWidth={2.5} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <label style={{ cursor: imageUploading ? 'default' : 'pointer', display: 'block' }}>
                                                            <div style={{ aspectRatio: '16/9', background: 'var(--bg-main)', border: '2px dashed var(--border)', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s', opacity: imageUploading ? 0.6 : 1 }}>
                                                                {imageUploading ? (
                                                                    <div className="loading-spinner" style={{ width: '24px', height: '24px', border: '3px solid rgba(var(--primary-rgb), 0.1)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                                                                ) : (
                                                                    <>
                                                                        <ImageIcon size={28} color="var(--text-muted)" style={{ opacity: 0.5 }} />
                                                                        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)' }}>Görsel yükle veya sürükle</span>
                                                                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', opacity: 0.6 }}>16:9 oranında kırpılır</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <input type="file" accept="image/*" onChange={handleAnnouncementImageUpload} style={{ display: 'none' }} disabled={imageUploading} />
                                                        </label>
                                                    )}
                                                </div>
                                            )}
                                            {currentTemplate.fields.includes('actionLabel') && (
                                                <>
                                                    <div>
                                                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>Buton Yazısı</label>
                                                        <input placeholder="Örn: Menüyü Gör" value={announcementForm.actionLabel}
                                                            onChange={e => setAnnouncementForm({ ...announcementForm, actionLabel: e.target.value })}
                                                            style={{ background: 'var(--bg-card)' }} />
                                                    </div>
                                                    <div>
                                                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>Yönlendirme Linki</label>
                                                        <input placeholder="Örn: /portal/menu" value={announcementForm.actionLink}
                                                            onChange={e => setAnnouncementForm({ ...announcementForm, actionLink: e.target.value })}
                                                            style={{ background: 'var(--bg-card)' }} />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Target Selection */}
                                    <div style={{ background: 'var(--bg-main)', padding: '1.5rem', borderRadius: '16px', border: '1px solid var(--border)' }}>
                                        <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Kime Gönderilsin?</label>
                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '0.75rem' }}>
                                            {[{ value: 'all', label: 'Tüm Müşteriler', icon: Users }, { value: 'groups', label: 'Seçili Gruplar', icon: Target }].map(opt => (
                                                <button key={opt.value} type="button"
                                                    onClick={() => setAnnouncementForm({ ...announcementForm, target: opt.value })}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '6px',
                                                        padding: '8px 16px', borderRadius: '10px',
                                                        border: announcementForm.target === opt.value ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
                                                        background: announcementForm.target === opt.value ? 'rgba(var(--primary-rgb), 0.08)' : 'var(--bg-card)',
                                                        color: announcementForm.target === opt.value ? 'var(--primary)' : 'var(--text-main)',
                                                        fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer', transition: 'all 0.15s'
                                                    }}
                                                >
                                                    <opt.icon size={14} /> {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                        {announcementForm.target === 'groups' && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '0.5rem' }}>
                                                <div>
                                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)' }}>Sadakat Seviyeleri</label>
                                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                        {(tenantData?.loyalty_levels || tenantData?.loyaltyLevels || [])
                                                            .filter(l => l.label && l.label.toLowerCase() !== 'loyal')
                                                            .map(level => {
                                                                const sel = announcementForm.loyaltyStatus.includes(level.label);
                                                                return (
                                                                    <button key={level.label} type="button"
                                                                        onClick={() => setAnnouncementForm({ ...announcementForm, loyaltyStatus: sel ? announcementForm.loyaltyStatus.filter(s => s !== level.label) : [...announcementForm.loyaltyStatus, level.label] })}
                                                                        style={{ padding: '5px 12px', fontSize: '0.75rem', fontWeight: 700, background: sel ? level.color : 'transparent', color: sel ? '#fff' : level.color, border: `1.5px solid ${level.color}`, borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s' }}>
                                                                        {level.label}
                                                                    </button>
                                                                );
                                                            })}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)' }}>Müşteri Rolleri</label>
                                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                        {(tenantData?.visit_roles || tenantData?.visitRoles || [])
                                                            .filter(r => r.label && r.label.toLowerCase() !== 'all')
                                                            .map(role => {
                                                                const sel = announcementForm.visitRole.includes(role.label);
                                                                return (
                                                                    <button key={role.label} type="button"
                                                                        onClick={() => setAnnouncementForm({ ...announcementForm, visitRole: sel ? announcementForm.visitRole.filter(r => r !== role.label) : [...announcementForm.visitRole, role.label] })}
                                                                        style={{ padding: '5px 12px', fontSize: '0.75rem', fontWeight: 700, background: sel ? role.color : 'transparent', color: sel ? 'white' : 'var(--text-main)', border: sel ? `1.5px solid ${role.color}` : '1.5px solid var(--border)', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s' }}>
                                                                        {role.label}
                                                                    </button>
                                                                );
                                                            })}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Live Preview */}
                                <div style={{ position: 'sticky', top: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.75rem', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', textAlign: 'center' }}>Önizleme</label>
                                    <AnnouncementPreview />
                                </div>
                            </div>

                            {/* Submit buttons */}
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                                <button type="button" onClick={() => { setAnnouncementModalOpen(false); setEditingId(null); }}
                                    style={{ flex: 1, background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-main)', fontWeight: 700, padding: '12px', borderRadius: '12px', cursor: 'pointer' }}>
                                    İptal
                                </button>
                                <button type="submit"
                                    className="btn-primary"
                                    style={{ flex: 2, fontWeight: 700, padding: '12px', borderRadius: '12px', border: 'none', boxShadow: '0 10px 20px rgba(var(--primary-rgb), 0.2)' }}>
                                    {editingId ? 'Güncelle' : 'Duyuru Oluştur'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                .hover-bg-success:hover { background: rgba(16, 185, 129, 0.2) !important; }
                .notification-card-hover:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(0,0,0,0.08); }
                @media (max-width: 768px) {
                    .announcement-modal-grid { grid-template-columns: 1fr !important; }
                }
                @media (max-width: 600px) {
                    .notification-modal { padding: 1.25rem !important; border-radius: 20px 20px 0 0 !important; }
                    .notifications-header-row { flex-direction: column !important; align-items: flex-start !important; }
                }
            `}</style>
        </div>
    );
};

// ─── Announcement Card Component ───
const AnnouncementCard = ({ notif, onBroadcast, onEdit, onDelete, t, tenantData }) => {
    const templateInfo = ANNOUNCEMENT_TEMPLATES.find(t => t.id === (notif.templateType || 'simple')) || ANNOUNCEMENT_TEMPLATES[0];

    return (
        <div className="card glass notification-card-hover" style={{ position: 'relative', transition: 'all 0.3s', padding: 0, overflow: 'hidden' }}>
            {notif.imageUrl && (
                <div style={{ width: '100%', aspectRatio: '16/9', position: 'relative', overflow: 'hidden' }}>
                    <img src={notif.imageUrl} alt={notif.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.4))' }} />
                </div>
            )}
            <div style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '36px', height: '36px', background: `${templateInfo.color}15`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <templateInfo.icon size={18} style={{ color: templateInfo.color }} />
                        </div>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: templateInfo.color, textTransform: 'uppercase' }}>{templateInfo.label}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => onBroadcast(notif)}
                            style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', padding: '8px 12px', borderRadius: '10px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', fontWeight: 700 }}
                            title="Push Bildirimi Gönder">
                            <Send size={14} /> GÖNDER
                        </button>
                        <button onClick={() => onEdit(notif)}
                            style={{ background: 'rgba(var(--primary-rgb), 0.05)', color: 'var(--primary)', padding: '8px', borderRadius: '10px', border: 'none', cursor: 'pointer' }}
                            title="Düzenle">
                            <Pencil size={16} />
                        </button>
                        <button onClick={() => onDelete(notif)}
                            style={{ background: 'transparent', color: '#ef4444', border: 'none', padding: '8px', cursor: 'pointer', borderRadius: '10px' }}>
                            <Trash2 size={16} />
                        </button>
                    </div>
                </div>

                <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem' }}>{notif.title}</h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: '1.25rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {notif.body}
                </p>

                {notif.actionLabel && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                        <Navigation size={14} /> Buton: <b style={{ color: 'var(--text-main)', fontWeight: 600 }}>{notif.actionLabel}</b>
                    </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.8125rem', color: 'var(--text-main)', marginBottom: '1rem' }}>
                    <Users size={14} style={{ color: 'var(--primary)' }} />
                    <span>{t('discounts.target')}: <b style={{ fontWeight: 600 }}>{
                        notif.target === 'all' ? t('discounts.targetAll')
                            : (() => {
                                const statuses = (Array.isArray(notif.loyaltyStatus) ? notif.loyaltyStatus : []).filter(s => s && s.toLowerCase() !== 'loyal');
                                const roles = (Array.isArray(notif.visitRole) ? notif.visitRole : []).filter(r => r && r.toLowerCase() !== 'loyal');
                                return [...statuses, ...roles].join(', ') || 'Özel Kitle';
                            })()
                    }</b></span>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ flex: 1, padding: '10px 8px', background: 'var(--bg-main)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '4px' }}>OLUŞTURULMA</div>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 700 }}>{notif.createdAt ? new Date(notif.createdAt).toLocaleDateString('tr-TR') : 'Yeni'}</div>
                    </div>
                    <div style={{ flex: 1, padding: '10px 8px', background: 'var(--bg-main)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: '4px' }}>DURUM</div>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: notif.broadcastedAt ? '#10b981' : 'var(--text-muted)' }}>
                            {notif.broadcastedAt ? 'GÖNDERİLDİ' : 'TASLAK'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Notifications;
